/**
 * 自动重载护栏 —— 部署自愈（版本更新 / chunk 加载失败）共用件。
 *
 * 任何「由系统主动 `location.reload()`」的路径都必须先过这里，防两件事：
 *   ① 把用户正在填的表单 / 未保存草稿刷掉（用户的最高约束：不影响现有功能）；
 *   ② 刷新后问题依旧时无限重载（死循环）。
 *
 * ⚠️ 本仓**没有全局的「未保存草稿」注册表**（2026-10-20 全仓核查结论）：
 *    · `beforeunload` 只有 `purchase-inquiry/detail/hooks/useInquiryDetail.ts`（回收预占单号）
 *      与 `process/designer.tsx`（beacon 上报）在用，都不表达「有草稿」；
 *    · 编辑态的唯一通用表达是 **URL 的 `mode=edit|add|copy`**（详情页普遍如此）；
 *    · 打开着的页签元数据在 sessionStorage 里、由 `@/utils/tagsViewStorage` 统一读写
 *      （键名常量只在那个文件里出现 —— 别在这里写出字面键名，否则会绊倒它的回归测试）；
 *    · keep-alive 缓存页里可能躺着未保存的编辑页（`TabLayout` 的缓存节点）。
 *    所以这里的原则是「**能证明安全才允许自动刷新**」：任一判据不满足就降级为
 *    「只提示、不自动刷新」，绝不为「自动刷新」去削弱草稿保护。
 *
 * 业务页若需要更精确地表达「我现在有未保存内容」，调用 {@link markUnsaved}（可选接入，
 * 不接也能工作 —— 那只是让护栏更保守）。
 */

import { TAGSVIEW_STORAGE_KEY } from './tagsViewStorage';

/** 全局「有未保存内容」标记键（sessionStorage；供业务页 opt-in） */
export const UNSAVED_FLAG_KEY = 'dingyan_unsaved_content';
/** 自动重载台账键（sessionStorage；按 scope 记次数，防死循环） */
const AUTO_RELOAD_MARKS_KEY = 'dingyan_auto_reload_marks';
/** 运维应急开关：置 1 后本会话只提示不自动刷新 */
const AUTO_RELOAD_OFF_KEY = 'dingyan_no_auto_reload';

/** 编辑态 URL 判据：详情页统一用 `?mode=edit|add|copy`（新增/复制同样在有输入的态） */
const EDIT_MODE_PATTERN = /[?&]mode=(edit|add|copy|create)(?:[&=]|$)/i;

/** 同一 scope 在一个会话里最多自动重载几次（超过只提示） */
const MAX_AUTO_RELOADS_PER_SCOPE = 2;

/** 自动刷新是否可用（总开关；置 false 即全局只提示不刷新） */
export const AUTO_RELOAD_ENABLED = true;

export type AutoReloadBlockReason =
  | 'disabled' // 总开关关掉 / 运维应急关闭
  | 'hidden' // 页面不可见（后台标签页）
  | 'pda' // PDA 手持页：随时可能正在扫码录入
  | 'editing' // 当前或任一已打开页签处于编辑态
  | 'editable-control' // 内联编辑和缓存页不一定使用 mode 参数
  | 'storage' // 无法可靠读取或保存刷新台账
  | 'overlay' // 有打开的 Modal / Drawer
  | 'uploading' // 有上传中的文件
  | 'unsaved' // 业务页显式标记了未保存内容
  | 'already-reloaded'; // 本会话该 scope 已自动重载过

const safeSession = (): Storage | null => {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // 隐私模式 / 存储被禁用
    return null;
  }
};

const readSession = (key: string): string | null => {
  const storage = safeSession();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string | null): boolean => {
  const storage = safeSession();
  if (!storage) return false;
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
    return storage.getItem(key) === value;
  } catch {
    return false;
  }
};

/**
 * 显式声明「当前有未保存内容」。业务页可在 dirty 时调 `markUnsaved(true)`，
 * 保存/离开时调 `markUnsaved(false)`。
 */
export function markUnsaved(on: boolean): void {
  writeSession(UNSAVED_FLAG_KEY, on ? '1' : null);
}

export function hasUnsavedMark(): boolean {
  return readSession(UNSAVED_FLAG_KEY) === '1';
}

/** URL（pathname + search）是否处于编辑态 */
export function isEditingUrl(url: string): boolean {
  return EDIT_MODE_PATTERN.test(url || '');
}

/**
 * 是否有**任一已打开页签**处于编辑态。
 * 页签被 keep-alive 缓存着，里面可能躺着填了一半的表单 —— 刷新会一并丢掉。
 */
function hasEditingTabOpen(): boolean {
  const raw = readSession(TAGSVIEW_STORAGE_KEY);
  if (!raw) return false;
  try {
    const tabs: unknown = JSON.parse(raw);
    if (!Array.isArray(tabs)) return true;
    return tabs.some(
      (tab) => !tab || typeof (tab as { path?: unknown }).path !== 'string' || isEditingUrl((tab as { path: string }).path),
    );
  } catch {
    // 解析失败按「有」处理：宁可只提示，也不冒险刷新
    return true;
  }
}

/** 是否有可见的 Modal / Drawer（antd 关闭态是 `.ant-modal-wrap { display: none }`） */
function hasOpenOverlay(): boolean {
  if (typeof document === 'undefined') return false;
  const wraps = document.querySelectorAll('.ant-modal-wrap, .ant-drawer-open');
  for (let i = 0; i < wraps.length; i += 1) {
    const el = wraps[i] as HTMLElement;
    const style = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null;
    if (style ? style.display !== 'none' && style.visibility !== 'hidden' : el.style.display !== 'none') {
      return true;
    }
  }
  return false;
}

/** 是否有上传中的文件（antd Upload 会给条目挂 uploading 类） */
function hasUploadingItem(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector('.ant-upload-list-item-uploading') !== null;
}

function hasEditableControl(): boolean {
  // 缓存页虽然不可见，仍可能保留未保存输入；不能仅检查当前可见控件。
  return Array.from(document.querySelectorAll('input, textarea, select, [contenteditable]')).some((node) => {
    if (node.hasAttribute('contenteditable') && node.getAttribute('contenteditable') !== 'false') return true;
    if (node.matches(':disabled, [readonly]')) return false;
    if (node.tagName === 'INPUT' && /^(hidden|button|submit|reset|image)$/i.test((node as HTMLInputElement).type)) return false;
    return node.matches('input, textarea, select');
  });
}

/** PDA 手持页（复用全局路由判据，避免第二套 path 前缀） */
function isPdaPath(pathname: string): boolean {
  return /^\/pda(?:\/|$)/.test(pathname || '');
}

/** 找出阻止自动刷新的一级原因；返回 null 表示「可安全自动刷新」 */
export function autoReloadBlockReason(): AutoReloadBlockReason | null {
  if (!AUTO_RELOAD_ENABLED || readSession(AUTO_RELOAD_OFF_KEY) === '1') return 'disabled';
  if (typeof window === 'undefined' || typeof document === 'undefined') return 'hidden';
  if (document.visibilityState !== 'visible') return 'hidden';
  try {
    const storage = safeSession();
    if (!storage) return 'storage';
    storage.getItem(AUTO_RELOAD_MARKS_KEY);
  } catch {
    return 'storage';
  }
  const currentPath = window.location.pathname || '';
  if (isPdaPath(currentPath)) return 'pda';
  if (isEditingUrl(`${currentPath}${window.location.search || ''}`)) return 'editing';
  if (hasEditingTabOpen()) return 'editing';
  if (hasUnsavedMark()) return 'unsaved';
  if (hasEditableControl()) return 'editable-control';
  if (hasOpenOverlay()) return 'overlay';
  if (hasUploadingItem()) return 'uploading';
  return null;
}

export function canAutoReload(): boolean {
  return autoReloadBlockReason() === null;
}

type AutoReloadMarks = Record<string, number>;

const readMarks = (): AutoReloadMarks => {
  const raw = readSession(AUTO_RELOAD_MARKS_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const marks: AutoReloadMarks = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) marks[key] = value;
    }
    return marks;
  } catch {
    return {};
  }
};

/**
 * 本会话内该 scope 是否还剩自动重载额度。
 * scope 建议用「构建标识」或固定名（如 `chunk`），这样每次发布都能重新获得一次机会，
 * 而同一个问题上永远不会无限刷。
 */
export function hasAutoReloadQuota(scope: string): boolean {
  return (readMarks()[scope] ?? 0) < MAX_AUTO_RELOADS_PER_SCOPE;
}

/** 记账：本次自动重载已发生。必须在 `reload()` 之前调用（reload 后代码不再执行） */
export function markAutoReload(scope: string): boolean {
  const marks = readMarks();
  marks[scope] = (marks[scope] ?? 0) + 1;
  return writeSession(AUTO_RELOAD_MARKS_KEY, JSON.stringify(marks));
}

/** 清空自动重载台账（登出等场景可调；当前无人调用，供后续接入） */
export function resetAutoReloadMarks(): void {
  writeSession(AUTO_RELOAD_MARKS_KEY, null);
}

/**
 * 统一的「申请一次自动重载」入口：先把护栏与额度都问一遍，
 * 允许则记账并 reload，不允许则返回原因交给调用方降级为提示。
 *
 * @returns null 表示已触发刷新（调用后本页即将卸载）；否则是阻止原因。
 */
export function requestAutoReload(scope: string): AutoReloadBlockReason | null {
  const reason = autoReloadBlockReason();
  if (reason) return reason;
  if (!hasAutoReloadQuota(scope)) return 'already-reloaded';
  if (!markAutoReload(scope)) return 'storage';
  window.location.reload();
  return null;
}

/**
 * 挂到 window 上的调试/接入入口。
 * 之所以给 window 而不只给模块导出：业务页往往不想为此加 import（并且部分页面是
 * 动态 import 的深层组件），window 上的钩子让「标记未保存」零成本接入。
 */
export function installReloadGuardApi(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as { __dingyanReloadGuard?: unknown }).__dingyanReloadGuard = {
    markUnsaved,
    hasUnsavedMark,
    canAutoReload,
    blockReason: autoReloadBlockReason,
    /** 运维/联调用：临时关闭自动刷新（本次会话） */
    disableAutoReload: () => writeSession(AUTO_RELOAD_OFF_KEY, '1'),
    enableAutoReload: () => writeSession(AUTO_RELOAD_OFF_KEY, null),
    resetMarks: resetAutoReloadMarks,
  };
}

/**
 * 「彻底刷新」：先反注册残留的 Service Worker 并清空 Cache Storage，再刷新。
 *
 * 为什么要多这一步：浏览器里若残留着**历史版本注册过的 Service Worker**（或某个
 * 中间层留下的 Cache Storage），它会拦在真实网络之前，于是「HTML 已经 no-cache、
 * 用户也按了 F5，看到的还是旧页面」——用户反馈里那句「需要刷新浏览器重新加载」，
 * 有一部分就是这个。清掉之后刷新才真正回到网络。
 *
 * 语义取自 `src/global.tsx` 里既有的 `clearCache()`（原代码里那段「反注册 SW +
 * clearCache」因 `defaultSettings.pwa === true` 永不执行，本模块把同一套语义
 * 收在一个显式入口里）。全仓仅 `global.tsx` 出现过 `navigator.serviceWorker` 与
 * `window.caches`，没有业务功能依赖它们，清理是安全的。
 *
 * ⚠️ 只在「用户显式点刷新」时调用：清缓存有代价，自动刷新路径仍走轻量的
 *    `location.reload()`（见 {@link requestAutoReload}）。
 *
 * @param timeoutMs 清理最多等这么久，超时也照样刷新（不能让用户点了没反应）
 */
export function hardReload(timeoutMs = 1500): void {
  const finish = () => window.location.reload();
  if (typeof window === 'undefined') return;

  const tasks: Promise<unknown>[] = [];
  try {
    // 用 globalThis 取用：极简环境（无 navigator）下可选链也不会抛 ReferenceError
    const registrations = globalThis.navigator?.serviceWorker?.getRegistrations?.();
    if (registrations) {
      tasks.push(
        registrations
          .then((list) => Promise.all(list.map((registration) => registration.unregister())))
          .catch(() => undefined),
      );
    }
  } catch {
    /* 不支持 SW 的浏览器 / 跨域 iframe 限制 */
  }
  try {
    const cacheStorage = globalThis.caches;
    if (cacheStorage?.keys) {
      tasks.push(
        cacheStorage
          .keys()
          .then((keys) => Promise.all(keys.map((key) => cacheStorage.delete(key))))
          .catch(() => undefined),
      );
    }
  } catch {
    /* Cache Storage 不可用（非 https / 隐私模式） */
  }

  if (tasks.length === 0) {
    finish();
    return;
  }
  Promise.race([
    Promise.all(tasks),
    new Promise((resolve) => {
      setTimeout(resolve, timeoutMs);
    }),
  ]).then(finish);
}
